export interface PollingOptions {
  intervalMs: number;
  maxAttempts: number;
  timeoutMs: number;
  signal?: AbortSignal;
}

export class PollingRequest<TParams, TResponse> {
  private attempts = 0;
  private startTime = 0;
  private readonly requestFn: (params: TParams[]) => Promise<TResponse>;
  private readonly isReadyCallback: (response: TResponse) => boolean;
  private readonly options: PollingOptions = {
    intervalMs: 2000,
    maxAttempts: 150,
    timeoutMs: 60000, // 10min
  };

  constructor(
    requestFn: (params: TParams[]) => Promise<TResponse>,
    isReadyCallback: (response: TResponse) => boolean,
    options?: Partial<PollingOptions>
  ) {
    this.requestFn = requestFn;
    this.isReadyCallback = isReadyCallback;
    this.options = {
      ...this.options,
      ...options,
    };
  }

  private hasExceededLimits(): boolean {
    if (this.options.maxAttempts && this.attempts > this.options.maxAttempts) {
      return true;
    }

    if (this.options.timeoutMs && Date.now() - this.startTime > this.options.timeoutMs) {
      return true;
    }

    return false;
  }

  private getLimitError(): string {
    if (this.options.maxAttempts && this.attempts > this.options.maxAttempts) {
      return `Max attempts (${this.options.maxAttempts}) exceeded`;
    }

    if (this.options.timeoutMs && Date.now() - this.startTime > this.options.timeoutMs) {
      return `Timeout (${this.options.timeoutMs}ms) exceeded`;
    }

    return 'Polling limits exceeded';
  }

  async execute(...params: TParams[]): Promise<TResponse> {
    this.attempts = 0;
    this.startTime = Date.now();

    return new Promise((resolve, reject) => {
      const poll = async () => {
        if (this.options.signal?.aborted) {
          return;
        }

        this.attempts++;

        if (this.hasExceededLimits()) {
          reject(new Error(this.getLimitError()));
          return;
        }

        try {
          const response = await this.requestFn(params);

          if (this.isReadyCallback(response)) {
            resolve(response);
          } else {
            setTimeout(poll, this.options.intervalMs);
          }
        } catch (error) {
          if (this.hasExceededLimits()) {
            reject(error);
          } else {
            setTimeout(poll, this.options.intervalMs);
          }
        }
      };

      poll();
    });
  }
}
